import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Edit, Plus, Save, Trash2, X, ChevronRight, ChevronDown, FolderOpen, Folder, List, PackageSearch, DollarSign, Check, ChevronUp, Settings2, ArrowUpDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { catalogService } from '../services/catalogService';
import { categoryService } from '../services/categoryService';
import { clientService } from '../services/clientService';
import { productService } from '../services/productService';
import { parseErrorMessage } from '../utils/helpers';

/* ───────────────────────────────────────────────────────────
   Configuración de tabs: cada entrada define servicio, campos
   de formulario y columnas de la tabla de listado.
   ─────────────────────────────────────────────────────────── */
const TAB_CONFIG = {
  countries: {
    label: 'Paises',
    service: catalogService.countries,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'ARG', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Argentina', required: true },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
    ],
  },
  clientTypes: {
    label: 'Tipo de clientes',
    service: catalogService.clientTypes,
    fields: [
      { key: 'name', label: 'Nombre', placeholder: 'Cliente', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  paymentMethods: {
    hidden: true,
    label: 'Forma de pago',
    service: catalogService.paymentMethods,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'TRANSFER', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Transferencia', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'uses_balance_payment', label: 'Balance Payment', type: 'checkbox' },
      { key: 'uses_advance', label: 'Anticipo', type: 'checkbox' },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'uses_balance_payment', label: 'Balance Payment', type: 'boolean' },
      { key: 'uses_advance', label: 'Anticipo', type: 'boolean' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  creditDays: {
    hidden: true,
    label: 'Dias de credito',
    service: catalogService.creditDays,
    fields: [
      { key: 'days', label: 'Dias', type: 'number', placeholder: '30', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'days', label: 'Dias' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  balancePayments: {
    hidden: true,
    label: 'Balance Payment',
    service: catalogService.balancePayments,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'SALDO', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Saldo pendiente', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  condicionPago: {
    label: 'Condición de Pago',
    combined: true,
    sections: [
      {
        key: 'paymentMethods',
        label: 'Forma de pago',
        service: catalogService.paymentMethods,
        fields: [
          { key: 'code', label: 'Codigo', placeholder: 'TRANSFER', required: true },
          { key: 'name', label: 'Nombre', placeholder: 'Transferencia', required: true },
          { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
          { key: 'uses_balance_payment', label: 'Balance Payment', type: 'checkbox' },
          { key: 'uses_advance', label: 'Anticipo', type: 'checkbox' },
          { key: 'is_active', label: 'Activo', type: 'checkbox' },
        ],
        columns: [
          { key: 'code', label: 'Codigo' },
          { key: 'name', label: 'Nombre' },
          { key: 'uses_balance_payment', label: 'Balance Payment', type: 'boolean' },
          { key: 'uses_advance', label: 'Anticipo', type: 'boolean' },
          { key: 'description', label: 'Descripcion' },
        ],
      },
      {
        key: 'creditDays',
        label: 'Días de crédito',
        service: catalogService.creditDays,
        fields: [
          { key: 'days', label: 'Dias', type: 'number', placeholder: '30', required: true },
          { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
          { key: 'is_active', label: 'Activo', type: 'checkbox' },
        ],
        columns: [
          { key: 'days', label: 'Dias' },
          { key: 'description', label: 'Descripcion' },
        ],
      },
      {
        key: 'balancePayments',
        label: 'Balance Payment',
        service: catalogService.balancePayments,
        fields: [
          { key: 'code', label: 'Codigo', placeholder: 'SALDO', required: true },
          { key: 'name', label: 'Nombre', placeholder: 'Saldo pendiente', required: true },
          { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
          { key: 'is_active', label: 'Activo', type: 'checkbox' },
        ],
        columns: [
          { key: 'code', label: 'Codigo' },
          { key: 'name', label: 'Nombre' },
          { key: 'description', label: 'Descripcion' },
        ],
      },
    ],
  },
  productCategories: {
    label: 'Categorias de producto',
    service: categoryService,
    isTree: true,
    fields: [
      { key: 'name', label: 'Nombre', placeholder: 'Ej: Juguetes', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'parent', label: 'Categoría padre', type: 'categoryParent' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  brands: {
    label: 'Marcas',
    service: catalogService.brands,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'ACME', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Acme', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  productLines: {
    label: 'Lineas de producto',
    service: catalogService.productLines,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'LINEA-1', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Linea 1', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  productTags: {
    label: 'Etiquetas',
    service: catalogService.productTags,
    fields: [
      { key: 'name', label: 'Nombre', placeholder: 'Novedad', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  packagingTypes: {
    label: 'Tipos de embalaje',
    service: catalogService.packagingTypes,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'BOX', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Caja', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  batteryTypes: {
    label: 'Tipos de bateria',
    service: catalogService.batteryTypes,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'AA', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'AA', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  factories: {
    label: 'Fabricas',
    service: catalogService.factories,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'FAB-01', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Fabrica 1', required: true },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
      { key: 'description', label: 'Descripcion' },
    ],
  },
  states: {
    label: 'Estados',
    service: catalogService.states,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'ARG-BA', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Buenos Aires', required: true },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
    ],
  },
  storeFormats: {
    label: 'Formatos de tienda',
    service: catalogService.storeFormats,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'HYP', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Hipermercado', required: true },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
    ],
  },
  fairs: {
    label: 'Ferias',
    service: catalogService.fairs,
    fields: [
      { key: 'code', label: 'Codigo', placeholder: 'TOY-FAIR', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Toy Fair NYC', required: true },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Codigo' },
      { key: 'name', label: 'Nombre' },
    ],
  },
  advancePayments: {
    label: 'Anticipo',
    service: catalogService.advancePayments,
    fields: [
      { key: 'advance_percentage', label: 'Porcentaje (%)', type: 'decimal', placeholder: '0.00', required: true },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'advance_percentage', label: 'Porcentaje (%)', type: 'percent' },
    ],
  },
  salespeople: {
    label: 'Vendedores',
    service: catalogService.salespeople,
    displayKey: 'full_name',
    fields: [
      { key: 'first_name', label: 'Nombre', placeholder: 'Juan', required: true },
      { key: 'last_name', label: 'Apellido', placeholder: 'Pérez', required: true },
      { key: 'email', label: 'Correo electrónico', placeholder: 'juan@empresa.com', type: 'email' },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'full_name', label: 'Nombre completo' },
      { key: 'email', label: 'Email' },
    ],
  },
  addressTypes: {
    label: 'Tipos de dirección',
    service: catalogService.addressTypes,
    fields: [
      { key: 'code', label: 'Código', placeholder: 'BILLING', required: true },
      { key: 'name', label: 'Nombre', placeholder: 'Facturación', required: true },
      { key: 'is_active', label: 'Activo', type: 'checkbox' },
    ],
    columns: [
      { key: 'code', label: 'Código' },
      { key: 'name', label: 'Nombre' },
    ],
  },
  priceLists: {
    label: 'Listas de precios',
    service: catalogService.priceLists,
    fields: [
      { key: 'name', label: 'Nombre', placeholder: 'Lista Cliente', required: true },
      { key: 'code', label: 'Codigo', placeholder: 'LP-CLI', required: true },
      { key: 'currency', label: 'Moneda', type: 'select', required: true },
      { key: 'category_id', label: 'Categoria de producto', type: 'select' },
      { key: 'client_id', label: 'Cliente', type: 'select' },
      { key: 'description', label: 'Descripcion', placeholder: 'Descripcion opcional', type: 'textarea' },
      { key: 'is_active', label: 'Activa', type: 'checkbox' },
    ],
    columns: [
      { key: 'name', label: 'Nombre' },
      { key: 'code', label: 'Codigo' },
      { key: 'currency', label: 'Moneda' },
      { key: 'category_name', label: 'Categoria' },
      { key: 'client_name', label: 'Cliente' },
    ],
  },
};

const DEFAULT_FORM = {
  name: '',
  code: '',
  description: '',
  currency: 'USD',
  days: '',
  category_id: '',
  client_id: '',
  parent: '',
  is_active: true,
  uses_balance_payment: false,
  uses_advance: false,
  requires_advance: false,
  advance_percentage: '',
  first_name: '',
  last_name: '',
  email: '',
};

/* ─── Componente de nodo del árbol de categorías ───────── */
const CategoryTreeNode = ({ node, level = 0, onEdit, onDelete, onAddChild }) => {
  const [expanded, setExpanded] = useState(level < 1);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100`}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-5 h-5 flex items-center justify-center text-gray-400 flex-shrink-0"
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : <span className="w-4" />}
        </button>
        {hasChildren ? (
          <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
        ) : (
          <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="text-sm font-medium text-gray-800 flex-1">{node.name}</span>
        {node.description && (
          <span className="text-xs text-gray-400 hidden md:inline truncate max-w-[200px]">{node.description}</span>
        )}
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          node.is_active === false ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
        }`}>
          {node.is_active === false ? 'Inactivo' : 'Activo'}
        </span>
        <span className="text-xs text-gray-400">Nivel {node.level + 1}</span>
        <div className="flex gap-1 ml-2">
          {node.level < 4 && (
            <button onClick={() => onAddChild(node)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Agregar subcategoría">
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => onEdit(node)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(node.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Eliminar">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <CategoryTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Helpers ─────────────────────────────────────────────── */

/** Normaliza cualquier respuesta de listado a un array plano. */
/* ─── Configuración de columnas disponibles para Lista de Precios ─── */
const ALL_PRICE_LIST_COLS = [
  { key: 'product_name',       label: 'Producto',       defaultOn: true,  sortable: true,  align: 'left',   editable: false },
  { key: 'product_code',       label: 'Código',         defaultOn: true,  sortable: true,  align: 'left',   editable: false, mono: true },
  { key: 'brand_name',         label: 'Marca',          defaultOn: false, sortable: true,  align: 'left',   editable: false },
  { key: 'category_name',      label: 'Categoría',      defaultOn: false, sortable: true,  align: 'left',   editable: false },
  { key: 'factory_name',       label: 'Fábrica',        defaultOn: false, sortable: true,  align: 'left',   editable: false },
  { key: 'product_variant',    label: 'Variante',       defaultOn: false, sortable: true,  align: 'left',   editable: false },
  { key: 'packaging_type_name',label: 'Embalaje',       defaultOn: false, sortable: true,  align: 'left',   editable: false },
  { key: 'pcs_per_ctn',        label: 'Pcs/Ctn',        defaultOn: false, sortable: true,  align: 'right',  editable: false },
  { key: 'cbm_per_ctn',        label: 'CBM/Ctn',        defaultOn: false, sortable: true,  align: 'right',  editable: false },
  { key: 'inners',             label: 'Inners',         defaultOn: false, sortable: true,  align: 'right',  editable: false },
  { key: 'moq',                label: 'MOQ',            defaultOn: false, sortable: true,  align: 'right',  editable: false },
  { key: 'cost_fob',           label: 'Costo FOB',      defaultOn: false, sortable: true,  align: 'right',  editable: false },
  { key: 'price_fob_unit',     label: 'Precio FOB',     defaultOn: true,  sortable: true,  align: 'right',  editable: true, type: 'number' },
  { key: 'is_active',          label: 'Activo',         defaultOn: true,  sortable: true,  align: 'center', editable: true, type: 'boolean' },
];

/* ─── Panel de productos de una Lista de Precios ──────────── */
const PriceListItemsPanel = ({ priceList, onClose }) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newForm, setNewForm] = useState({ product_id: '', price_fob_unit: '' });

  // Edición inline: { id, field, value }
  const [editing, setEditing] = useState(null);

  // Columnas visibles (persistidas por lista)
  const storageKey = `priceListCols_${priceList.id}`;
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (saved && typeof saved === 'object') return saved;
    } catch {}
    return Object.fromEntries(ALL_PRICE_LIST_COLS.map(c => [c.key, c.defaultOn]));
  });
  const [showColPanel, setShowColPanel] = useState(false);
  const colPanelRef = useRef(null);

  // Ordenamiento
  const [sortCol, setSortCol] = useState('product_name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(visibleCols)); } catch {}
  }, [visibleCols, storageKey]);

  // Cerrar panel de columnas al hacer click afuera
  useEffect(() => {
    const handler = e => {
      if (colPanelRef.current && !colPanelRef.current.contains(e.target)) setShowColPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsData, productsData] = await Promise.all([
        catalogService.priceLists.getItems(priceList.id),
        productService.getAll(),
      ]);
      setItems(Array.isArray(itemsData) ? itemsData : itemsData?.results || []);
      const allProducts = Array.isArray(productsData) ? productsData : productsData?.results || [];
      setProducts(allProducts.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (e) {
      setError('Error cargando productos de la lista.');
    } finally {
      setLoading(false);
    }
  }, [priceList.id]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const existingProductIds = useMemo(() => new Set(items.map(i => i.product)), [items]);
  const availableProducts = useMemo(
    () => products.filter(p => !existingProductIds.has(p.id)),
    [products, existingProductIds]
  );

  // Ordenar items
  const sortedItems = useMemo(() => {
    const col = ALL_PRICE_LIST_COLS.find(c => c.key === sortCol);
    if (!col) return items;
    return [...items].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (va == null) va = col.align === 'right' ? -Infinity : '';
      if (vb == null) vb = col.align === 'right' ? -Infinity : '';
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [items, sortCol, sortDir]);

  const activeCols = ALL_PRICE_LIST_COLS.filter(c => visibleCols[c.key]);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newForm.product_id || !newForm.price_fob_unit) return;
    setSaving(true); setError('');
    try {
      await catalogService.priceLists.addItem(priceList.id, {
        product: parseInt(newForm.product_id),
        price_fob_unit: parseFloat(newForm.price_fob_unit),
      });
      setNewForm({ product_id: '', price_fob_unit: '' });
      await loadItems();
    } catch (e) {
      setError(parseErrorMessage(e, 'No se pudo agregar el producto.'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item, field) => {
    setEditing({ id: item.id, field, value: item[field] ?? '' });
  };

  const commitEdit = async () => {
    if (!editing) return;
    const item = items.find(i => i.id === editing.id);
    if (!item) { setEditing(null); return; }
    const col = ALL_PRICE_LIST_COLS.find(c => c.key === editing.field);
    if (!col?.editable) { setEditing(null); return; }

    let payload = {};
    if (editing.field === 'price_fob_unit') {
      const val = parseFloat(editing.value);
      if (isNaN(val)) { setEditing(null); return; }
      payload = { price_fob_unit: val };
    } else if (editing.field === 'is_active') {
      payload = { is_active: editing.value };
    }

    setSaving(true);
    try {
      await catalogService.priceLists.updateItem(priceList.id, editing.id, payload);
      setEditing(null);
      await loadItems();
    } catch (e) {
      setError(parseErrorMessage(e, 'No se pudo guardar.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Quitar "${item.product_name}" de la lista?`)) return;
    setSaving(true);
    try {
      await catalogService.priceLists.deleteItem(priceList.id, item.id);
      await loadItems();
    } catch (e) {
      setError(parseErrorMessage(e, 'No se pudo eliminar.'));
    } finally {
      setSaving(false);
    }
  };

  const renderCell = (item, col) => {
    const isEditing = editing?.id === item.id && editing?.field === col.key;
    const val = item[col.key];

    if (col.type === 'boolean') {
      return (
        <button
          disabled={saving}
          onClick={() => {
            catalogService.priceLists.updateItem(priceList.id, item.id, { is_active: !item.is_active })
              .then(loadItems).catch(e => setError(parseErrorMessage(e)));
          }}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mx-auto transition-colors ${
            item.is_active ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-300'
          }`}
          title={item.is_active ? 'Desactivar' : 'Activar'}
        >
          {item.is_active && <Check className="w-3 h-3" />}
        </button>
      );
    }

    if (!col.editable) {
      if (val == null || val === '') return <span className="text-gray-300">—</span>;
      if (col.mono) return <span className="font-mono text-xs text-gray-500">{val}</span>;
      return <span>{String(val)}</span>;
    }

    // Editable: price_fob_unit
    if (isEditing) {
      return (
        <input
          type="number"
          step="0.000001"
          min="0"
          value={editing.value}
          autoFocus
          onChange={e => setEditing(ed => ({ ...ed, value: e.target.value }))}
          onBlur={commitEdit}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditing(null);
          }}
          className="w-28 border border-blue-400 rounded px-2 py-0.5 text-sm text-right focus:ring-2 focus:ring-blue-500 bg-white"
        />
      );
    }

    return (
      <span
        className="cursor-pointer px-1 rounded hover:bg-blue-100 font-semibold text-green-700 transition-colors"
        title="Click para editar"
        onClick={() => startEdit(item, col.key)}
      >
        {val != null ? parseFloat(val).toFixed(4) : '—'}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-blue-50 border border-blue-200 rounded-xl p-5 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PackageSearch className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-blue-800">
            Productos de: <span className="font-bold">{priceList.name}</span>
            <span className="ml-2 text-xs font-normal text-blue-500">({priceList.currency})</span>
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Selector de columnas */}
          <div className="relative" ref={colPanelRef}>
            <button
              onClick={() => setShowColPanel(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
              title="Columnas visibles"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Columnas
            </button>
            {showColPanel && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 min-w-52">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Columnas visibles</p>
                <div className="space-y-1">
                  {ALL_PRICE_LIST_COLS.map(col => (
                    <label key={col.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!visibleCols[col.key]}
                        onChange={e => setVisibleCols(v => ({ ...v, [col.key]: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-blue-400 hover:text-blue-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>
      )}

      {/* Formulario para agregar producto */}
      <form onSubmit={handleAdd} className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-600 mb-1">Producto</label>
          <select
            value={newForm.product_id}
            onChange={e => setNewForm(f => ({ ...f, product_id: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">— Seleccionar —</option>
            {availableProducts.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} {p.product_code ? `(${p.product_code})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="w-40">
          <label className="block text-xs font-medium text-gray-600 mb-1">Precio FOB</label>
          <input
            type="number" step="0.000001" min="0"
            value={newForm.price_fob_unit}
            onChange={e => setNewForm(f => ({ ...f, price_fob_unit: e.target.value }))}
            placeholder="0.000000"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Agregar
        </button>
      </form>

      {/* Tabla */}
      {loading ? (
        <div className="text-center text-gray-500 py-6 text-sm">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center text-gray-400 py-6 text-sm">
          <PackageSearch className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Esta lista no tiene productos. Agregá uno arriba.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-blue-200">
          <table className="w-full text-sm">
            <thead className="bg-blue-100">
              <tr>
                {activeCols.map(col => (
                  <th
                    key={col.key}
                    className={`px-3 py-2 font-semibold text-blue-800 whitespace-nowrap select-none ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    } ${col.sortable ? 'cursor-pointer hover:bg-blue-200 transition-colors' : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.sortable && sortCol === col.key ? (
                        sortDir === 'asc'
                          ? <ChevronUp className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />
                      ) : col.sortable ? (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      ) : null}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 text-center font-semibold text-blue-800">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-blue-100">
              {sortedItems.map(item => (
                <tr key={item.id} className={`transition-colors ${item.is_active ? 'hover:bg-blue-50/60' : 'opacity-50 bg-gray-50'}`}>
                  {activeCols.map(col => (
                    <td
                      key={col.key}
                      className={`px-3 py-2 ${
                        col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''
                      }`}
                    >
                      {renderCell(item, col)}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <button
                        onClick={() => handleDelete(item)}
                        disabled={saving}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Quitar producto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-500">
            {sortedItems.length} producto{sortedItems.length !== 1 ? 's' : ''} · Click en <strong>Precio FOB</strong> para editar · Click en encabezados para ordenar
          </div>
        </div>
      )}
    </motion.div>
  );
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    // Buscar primer valor que sea array
    const firstArray = Object.values(data).find(Array.isArray);
    if (firstArray) return firstArray;
  }
  return [];
};

/* ─── Genera el próximo código correlativo a partir de los items existentes ─── */
const nextAutoCode = (items) => {
  const nums = (items || [])
    .map((i) => {
      const raw = String(i.code || '').trim();
      const onlyDigits = raw.replace(/\D/g, '');
      return onlyDigits ? parseInt(onlyDigits, 10) : 0;
    })
    .filter((n) => !isNaN(n));
  const max = nums.length ? Math.max(...nums) : 0;
  const next = max + 1;
  return String(next).padStart(Math.max(3, String(max).length), '0');
};

/* ─── CatalogSection: sub-componente para tabs combinados ──── */

const CatalogSection = ({ config }) => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [formData, setFormData]     = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await config.service.getAll();
      setItems(Array.isArray(data) ? data : data?.results ?? []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [config.service]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setFormData({});
    setEditingItem(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (item) => {
    setFormData({ ...item });
    setEditingItem(item);
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try { await config.service.delete(id); load(); }
    catch (e) { alert(parseErrorMessage(e)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = { ...formData };
    // Convertir is_active
    if (payload.is_active === undefined) payload.is_active = true;
    try {
      if (editingItem?.id) {
        await config.service.update(editingItem.id, payload);
      } else {
        await config.service.create(payload);
      }
      resetForm();
      load();
    } catch (e) { setError(parseErrorMessage(e)); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-700 text-sm">{config.label}</h3>
        <button
          onClick={() => { setFormData({}); setEditingItem(null); setError(''); setShowForm(true); }}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
        >
          <Plus className="w-3.5 h-3.5" /> Nuevo
        </button>
      </div>

      {/* Formulario inline */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-5 py-4 bg-blue-50 border-b border-blue-100"
          >
            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              {config.fields.filter(f => f.type !== 'checkbox').map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      rows={2}
                      value={formData[field.key] ?? ''}
                      onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={formData[field.key] ?? ''}
                      onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      required={field.required}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  )}
                </div>
              ))}
              {/* Checkbox activo */}
              {config.fields.some(f => f.type === 'checkbox') && (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active !== false}
                    onChange={e => setFormData(p => ({ ...p, is_active: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  Activo
                </label>
              )}
              <div className="flex gap-2">
                <button type="submit" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg flex items-center gap-1">
                  <Save className="w-3.5 h-3.5" /> {editingItem ? 'Guardar' : 'Crear'}
                </button>
                <button type="button" onClick={resetForm} className="px-3 py-1.5 bg-white border border-gray-300 text-gray-600 text-xs font-medium rounded-lg flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabla */}
      {loading ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">Sin registros. Hacé clic en "Nuevo" para agregar.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {config.columns.map(col => (
                <th key={col.key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col.label}</th>
              ))}
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                {config.columns.map(col => (
                  <td key={col.key} className="px-4 py-2 text-gray-700">{item[col.key] ?? '-'}</td>
                ))}
                <td className="px-4 py-2 text-center">
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${item.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {item.is_active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleEdit(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Eliminar">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ─── Componente ──────────────────────────────────────────── */

const CatalogsManager = ({ initialTab, allowedTabs } = {}) => {
  const defaultTab = (allowedTabs && allowedTabs.length > 0) ? allowedTabs[0] : 'countries';
  const [activeTab, setActiveTab] = useState(initialTab && (!allowedTabs || allowedTabs.includes(initialTab)) ? initialTab : defaultTab);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [expandedPriceList, setExpandedPriceList] = useState(null);
  const latestLoadIdRef = useRef(0);

  const tabConfig = TAB_CONFIG[activeTab];

  const currencyOptions = useMemo(() => ([
    { value: 'USD', label: 'USD' },
    { value: 'ARS', label: 'ARS' },
    { value: 'EUR', label: 'EUR' },
  ]), []);

  /* ── Carga de datos auxiliares (categorías / clientes) ── */
  const loadAuxiliaryData = useCallback(async () => {
    try {
      const [categoryData, clientData] = await Promise.all([
        categoryService.getAll({ active_only: false }),
        clientService.getAll({}),
      ]);
      setCategories(normalizeList(categoryData));
      setClients(normalizeList(clientData));
    } catch (error) {
      console.error('Error cargando datos auxiliares:', error);
    }
  }, []);

  /* ── Carga de items del tab activo ── */
  const loadItems = useCallback(async (tabKey = activeTab) => {
    const selectedConfig = TAB_CONFIG[tabKey];
    const loadId = ++latestLoadIdRef.current;

    if (!selectedConfig?.service?.getAll) {
      setItems([]);
      setIsLoading(false);
      return [];
    }

    setItems([]);
    setIsLoading(true);
    try {
      const response = await selectedConfig.service.getAll({ active_only: false });

      // Si otra llamada más nueva ya empezó, descartar esta.
      if (loadId !== latestLoadIdRef.current) return [];

      const normalized = normalizeList(response);
      setItems(normalized);
      setErrorMessage('');
      return normalized;
    } catch (error) {
      if (loadId !== latestLoadIdRef.current) return [];
      console.error('Error cargando catalogo:', error);
      setItems([]);
      setErrorMessage(error?.message || 'Error al cargar el catálogo');
      return [];
    } finally {
      // Solo desactivar loading si esta sigue siendo la carga vigente.
      if (loadId === latestLoadIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [activeTab]);

  const loadFlatCategories = useCallback(async () => {
    try {
      const data = await categoryService.getFlat({ active_only: false });
      setFlatCategories(normalizeList(data));
    } catch (error) {
      console.error('Error cargando categorías planas:', error);
      setFlatCategories([]);
    }
  }, []);

  /* ── Efecto: cambio de tab ── */
  useEffect(() => {
    loadItems(activeTab);
    if (activeTab === 'priceLists') {
      loadAuxiliaryData();
    }
    if (activeTab === 'productCategories') {
      loadFlatCategories();
    }
    setShowForm(false);
    setEditingItem(null);
    setFormData(DEFAULT_FORM);
    setErrorMessage('');
    setExpandedPriceList(null);
  }, [activeTab]);

  /* ── Efecto: tab inicial desde props ── */
  useEffect(() => {
    if (initialTab && TAB_CONFIG[initialTab] && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  /* ── Acciones de formulario ── */
  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      code: item.code || '',
      description: item.description || '',
      currency: item.currency || 'USD',
      days: item.days !== undefined && item.days !== null ? String(item.days) : '',
      category_id: item.category_id ? String(item.category_id) : '',
      client_id: item.client_id ? String(item.client_id) : '',
      parent: item.parent ? String(item.parent) : '',
      is_active: item.is_active !== false,
      uses_balance_payment: item.uses_balance_payment === true,
      uses_advance: item.uses_advance === true,
      requires_advance: item.requires_advance === true,
      advance_percentage: item.advance_percentage !== undefined && item.advance_percentage !== null ? String(item.advance_percentage) : '',
    });
    setShowForm(true);
  };

  const handleAddChild = (parentNode) => {
    resetForm();
    setFormData((prev) => ({ ...prev, parent: String(parentNode.id) }));
    setShowForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('¿Deseas eliminar este registro?')) return;
    try {
      await tabConfig.service.delete(itemId);
      // Quitar el item del state inmediatamente (optimistic)
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      // Luego recargar por si hay cambios en el backend
      await loadItems();
    } catch (error) {
      console.error('Error eliminando registro:', error);
      alert(`No se pudo eliminar el registro.\n${parseErrorMessage(error)}`);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {};

    tabConfig.fields.forEach((field) => {
      const key = field.key;
      const rawValue = formData[key];
      if (field.type === 'checkbox') {
        payload[key] = rawValue !== false;
        return;
      }
      if (field.type === 'number') {
        payload[key] = rawValue !== '' && rawValue !== null && rawValue !== undefined
          ? parseInt(rawValue, 10)
          : null;
        return;
      }
      if (field.type === 'decimal') {
        payload[key] = rawValue !== '' && rawValue !== null && rawValue !== undefined
          ? parseFloat(rawValue)
          : null;
        return;
      }
      if (field.type === 'select') {
        if (key === 'category_id' || key === 'client_id') {
          payload[key] = rawValue ? parseInt(rawValue, 10) : null;
          return;
        }
        payload[key] = rawValue || '';
        return;
      }
      if (field.type === 'categoryParent') {
        payload[key] = rawValue ? parseInt(rawValue, 10) : null;
        return;
      }
      if (key === 'description') {
        payload[key] = rawValue?.trim() || null;
        return;
      }
      payload[key] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    });

    if (activeTab === 'countries' && payload.code) {
      payload.code = payload.code.toUpperCase();
    }

    // Anticipo: el nombre se deriva del %; requiere anticipo si % > 0
    if (activeTab === 'advancePayments') {
      const pct = payload.advance_percentage != null && !Number.isNaN(payload.advance_percentage)
        ? Number(payload.advance_percentage)
        : 0;
      payload.advance_percentage = pct;
      payload.requires_advance = pct > 0;
      payload.name = `${pct}%`;
    }

    try {
      let savedItem = null;

      if (editingItem) {
        savedItem = await tabConfig.service.update(editingItem.id, payload);
      } else {
        savedItem = await tabConfig.service.create(payload);
      }

      resetForm();
      setShowForm(false);

      // Recargar lista desde backend
      const refreshedItems = await loadItems();
      if (activeTab === 'productCategories') {
        loadFlatCategories();
      }

      // ── Fallback optimista ──
      // Si el backend devolvió lista vacía o no incluye el registro
      // guardado, lo insertamos/actualizamos manualmente para que el
      // usuario lo vea de inmediato.
      if (savedItem?.id) {
        const found = (refreshedItems || []).some(
          (i) => String(i.id) === String(savedItem.id)
        );
        if (!found) {
          setItems((prev) => {
            const exists = prev.some((i) => String(i.id) === String(savedItem.id));
            if (exists) {
              return prev.map((i) =>
                String(i.id) === String(savedItem.id) ? savedItem : i
              );
            }
            return [savedItem, ...prev];
          });
        }
      }
    } catch (error) {
      console.error('Error guardando registro:', error);
      const message = parseErrorMessage(error, 'No se pudo guardar el registro. Revisá los datos e intentá de nuevo.');
      setErrorMessage(message);
      alert(message);
    }
  };

  /* ── Renderizado de campo de formulario ── */
  const renderField = (field) => {
    const value = formData[field.key] ?? '';
    const commonProps = {
      id: field.key,
      name: field.key,
      value,
      onChange: (event) => {
        const nextValue = field.type === 'checkbox'
          ? event.target.checked
          : event.target.value;
        setFormData((prev) => ({ ...prev, [field.key]: nextValue }));
      },
      className: 'w-full text-sm px-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500',
    };

    if (field.type === 'textarea') {
      return <textarea {...commonProps} rows={3} placeholder={field.placeholder} />;
    }

    if (field.type === 'select') {
      let options = [];
      if (field.key === 'currency') {
        options = currencyOptions;
      } else if (field.key === 'category_id') {
        options = categories.map((cat) => ({ value: String(cat.id), label: cat.name }));
      } else if (field.key === 'client_id') {
        options = clients.map((client) => ({
          value: String(client.id),
          label: client.email ? `${client.name} (${client.email})` : client.name,
        }));
      }
      return (
        <select {...commonProps}>
          <option value="">Seleccionar</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'categoryParent') {
      const editingId = editingItem?.id ? String(editingItem.id) : null;
      const availableParents = flatCategories
        .filter((cat) => cat.level < 4 && String(cat.id) !== editingId);
      return (
        <select {...commonProps}>
          <option value="">Sin categoría padre (raíz)</option>
          {availableParents.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {'─'.repeat(cat.level)} {cat.full_path || cat.name}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'decimal') {
      return (
        <input
          {...commonProps}
          type="number"
          step="0.01"
          min="0"
          max={field.key === 'advance_percentage' ? '100' : undefined}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(formData[field.key])}
            onChange={(event) => {
              setFormData((prev) => ({ ...prev, [field.key]: event.target.checked }));
            }}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">{field.label}</span>
        </label>
      );
    }

    if (field.key === 'code' && !editingItem) {
      return (
        <div className="relative">
          <input
            {...commonProps}
            type="text"
            placeholder={field.placeholder}
            required={field.required}
            className={`${commonProps.className} pr-24`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 select-none pointer-events-none">
            auto · editable
          </span>
        </div>
      );
    }

    return <input {...commonProps} type={field.type || 'text'} placeholder={field.placeholder} required={field.required} />;
  };

  /* ── Columnas de la tabla: las definidas + Estado + Acciones ── */
  const tableColumns = tabConfig.columns || [];

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* ── Tabs ── */}
      <div className="flex flex-wrap gap-4 border-b border-gray-200 mb-6">
        {Object.entries(TAB_CONFIG).filter(([key, cfg]) => !cfg.hidden && (!allowedTabs || allowedTabs.includes(key))).map(([key, config]) => (
          <button
            key={key}
            onClick={() => {
              if (key === activeTab) {
                loadItems(key);
                if (key === 'priceLists') loadAuxiliaryData();
                return;
              }
              setActiveTab(key);
            }}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === key
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {config.label}
          </button>
        ))}
      </div>

      {/* ── Tab combinado: Condición de Pago ── */}
      {tabConfig.combined && (
        <div className="space-y-8">
          <h2 className="text-xl font-bold text-gray-800">{tabConfig.label}</h2>
          {tabConfig.sections.map(section => (
            <CatalogSection key={section.key} config={section} />
          ))}
        </div>
      )}

      {/* ── Contenido tab normal ── */}
      {!tabConfig.combined && <>

      {/* ── Header + botón Nuevo ── */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">{tabConfig.label}</h2>
        <button
          onClick={() => {
            resetForm();
            // Pre-rellenar código correlativo sólo si el tab usa campo 'code'
            const hasCodeField = tabConfig.fields?.some((f) => f.key === 'code');
            if (hasCodeField) {
              setFormData((prev) => ({ ...prev, code: nextAutoCode(items) }));
            }
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo
        </button>
      </div>

      {/* ── Formulario (crear / editar) ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
          >
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tabConfig.fields.map((field) => (
                <div key={field.key} className={field.type === 'checkbox' ? 'md:col-span-2' : ''}>
                  {field.type !== 'checkbox' && (
                    <label htmlFor={field.key} className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>
                  )}
                  {renderField(field)}
                </div>
              ))}

              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Error ── */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {/* ── Listado ── */}
      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No hay registros</div>
      ) : tabConfig.isTree ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Categorías ({flatCategories.length} total)
          </div>
          {items.map((node) => (
            <CategoryTreeNode
              key={node.id}
              node={node}
              level={0}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddChild={handleAddChild}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {tableColumns.map((col) => (
                    <th
                      key={col.key}
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                    >
                      {col.label}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    {tableColumns.map((col) => (
                      <td key={col.key} className="px-6 py-4">
                        <div className="text-sm text-gray-800">
                          {col.type === 'boolean'
                            ? (item[col.key]
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Sí</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">No</span>)
                            : col.type === 'percent'
                            ? (item[col.key] !== undefined && item[col.key] !== null ? `${item[col.key]}%` : '-')
                            : (item[col.key] !== undefined && item[col.key] !== null ? String(item[col.key]) : '-')}
                        </div>
                      </td>
                    ))}
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.is_active === false
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {item.is_active === false ? 'Inactivo' : 'Activo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        {activeTab === 'priceLists' && (
                          <button
                            onClick={() => setExpandedPriceList(
                              expandedPriceList?.id === item.id ? null : item
                            )}
                            className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium ${
                              expandedPriceList?.id === item.id
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-purple-600 hover:bg-purple-50'
                            }`}
                            title="Ver / editar productos de esta lista"
                          >
                            <List className="w-4 h-4" />
                            Productos
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </>}

      {/* ── Panel de productos de la lista de precios seleccionada ── */}
      <AnimatePresence>
        {activeTab === 'priceLists' && expandedPriceList && (
          <PriceListItemsPanel
            key={expandedPriceList.id}
            priceList={expandedPriceList}
            onClose={() => setExpandedPriceList(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CatalogsManager;
